import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight, Play, Shield, Brain, CheckSquare, BarChart3,
  Zap, Eye, Users, Building2, Lock, FileText, Layers, Globe,
  Code, Settings, Server, Briefcase, Heart, Landmark,
  ChevronRight, Mail, MapPin, Phone, ExternalLink, MessageSquare,
  Upload, AlertTriangle, Bell, Palette, Video, Sparkles, Radio
} from 'lucide-react';
import { ChatWidget } from '../ChatWidget';

const APP_URL = '';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' }, transition: { duration: 0.5 } };
const stagger = { initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: '-40px' }, variants: { visible: { transition: { staggerChildren: 0.08 } } } };
const cardVariant = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

/* ═══════════════════════════════════════════════
   D3 Command Official Brand Logo (Image Asset)
   ═══════════════════════════════════════════════ */
const D3CommandLogo = ({ size = 'default', className = '' }) => {
  const heights = { sm: 28, default: 36, lg: 44, hero: 64 };
  const h = heights[size] || 36;
  return (
    <img
      src="/d3-logo-transparent.png"
      alt="D3 Command"
      height={h}
      style={{ height: `${h}px`, width: 'auto' }}
      className={className}
      data-testid={`d3-brand-logo-${size}`}
      draggable={false}
    />
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Hero
   ═══════════════════════════════════════════════ */
const Hero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="mktg-hero">
      {/* BG layers */}
      <div className="absolute inset-0 bg-[#0B1120]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(37,99,235,0.18),transparent)]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-600/[0.06] rounded-full blur-[140px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center py-32">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-medium text-gray-400 tracking-wide">Try our Interactive Demo — No signup required</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08 }}
          className="mb-8 flex justify-center" data-testid="mktg-hero-logo">
          <D3CommandLogo size="hero" />
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6">
          AI-Powered Apps, Automation,{' '}
          <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">and Custom Software Solutions</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          We help visionary leaders and enterprise teams build high-performance web and mobile apps, automate workflows, and deploy with confidence.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap items-center justify-center gap-4">
          <a href="https://app.d3command.com/demo"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-semibold text-sm shadow-[0_0_24px_rgba(79,70,229,0.25)] hover:shadow-[0_0_32px_rgba(79,70,229,0.4)] transition-all" data-testid="mktg-hero-try-demo">
            <Play className="w-4 h-4" /> Try Interactive Demo
          </a>
          <a href="#demo" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/[0.06] border border-white/10 hover:border-white/20 hover:bg-white/[0.08] text-white font-medium text-sm transition-all" data-testid="mktg-hero-request-demo">
            Get a Custom Quote <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#services" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/[0.04] border border-white/[0.06] hover:border-indigo-500/30 text-gray-300 hover:text-white font-medium text-sm transition-all">
            Explore Services <ChevronRight className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Demo features preview */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-6 mt-12 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-indigo-400" /> Auto-narrated walkthrough</span>
          <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-cyan-400" /> Live AI insights</span>
          <span className="flex items-center gap-1.5"><Radio className="w-3.5 h-3.5 text-green-400" /> Real-time sync</span>
        </motion.div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Flagship Platform
   ═══════════════════════════════════════════════ */
const platformSteps = [
  { icon: Upload, label: 'Upload', desc: 'Meeting or audio' },
  { icon: Brain, label: 'AI Processing', desc: 'Transcribe & analyze' },
  { icon: CheckSquare, label: 'Tasks & Risks', desc: 'Auto-extracted' },
  { icon: BarChart3, label: 'Dashboard', desc: 'Real-time visibility' },
  { icon: Bell, label: 'Alerts', desc: 'AI-powered insights' },
];

const platformFeatures = [
  { icon: Brain, title: 'AI Meeting Intelligence', desc: 'Transcribe conversations and extract key points, decisions, and action items automatically.' },
  { icon: Sparkles, title: 'Live AI Insights', desc: 'Real-time productivity alerts, risk patterns, and context-aware recommendations that surface as you work.' },
  { icon: Zap, title: 'One-Click Quick Actions', desc: 'Upload meetings, create tasks, log risks, or add policies instantly — streamlined workflows in one click.' },
  { icon: Radio, title: 'Real-Time Collaboration', desc: 'Live presence indicators, WebSocket sync, and instant updates across all team members.' },
  { icon: MessageSquare, title: 'Threaded Comments & @Mentions', desc: 'Unlimited-depth threaded discussions with @mention notifications and email reply-to-comment pipeline.' },
  { icon: Mail, title: 'Activity Digest Emails', desc: 'Opt into daily or weekly digests summarizing mentions, completed tasks, and risk updates.' },
];

const FlagshipPlatform = () => (
  <section id="platform" className="relative py-28 overflow-hidden" data-testid="mktg-platform">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-500/[0.02] to-transparent" />
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Flagship Platform</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">The platform that turns conversations into execution</h2>
        <p className="text-gray-400 max-w-xl mx-auto">From meeting room to dashboard — every decision, task, and risk captured and tracked automatically.</p>
      </motion.div>

      {/* Pipeline flow */}
      <motion.div {...fadeUp} className="flex items-center justify-center gap-2 mb-20 flex-wrap">
        {platformSteps.map((step, i) => (
          <div key={step.label} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] min-w-[110px]">
              <step.icon className="w-5 h-5 text-indigo-400" />
              <span className="text-xs font-semibold text-white">{step.label}</span>
              <span className="text-[10px] text-gray-500">{step.desc}</span>
            </div>
            {i < platformSteps.length - 1 && <ArrowRight className="w-4 h-4 text-gray-700 flex-shrink-0" />}
          </div>
        ))}
      </motion.div>

      {/* Feature grid */}
      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {platformFeatures.map((f) => (
          <motion.div key={f.title} variants={cardVariant} className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/20 transition-colors group">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-4 group-hover:bg-indigo-500/15 transition-colors">
              <f.icon className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="text-white font-semibold text-sm mb-2">{f.title}</h3>
            <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Try Demo CTA */}
      <motion.div {...fadeUp} className="flex flex-col items-center mt-12">
        <a href="https://app.d3command.com/demo" 
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors">
          <Play className="w-4 h-4" /> Try Interactive Demo
        </a>
        <p className="text-gray-500 text-xs mt-3">No signup required • Auto-narrated walkthrough included</p>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Interactive Demo Showcase
   ═══════════════════════════════════════════════ */
const demoFeatures = [
  { icon: Video, title: 'Watch Demo', desc: 'Auto-narrated 2-minute guided walkthrough with professional voiceover' },
  { icon: Eye, title: 'Take Tour', desc: 'Step-by-step interactive product tour highlighting key features' },
  { icon: Play, title: 'Record Demo Video', desc: 'Export your demo session as MP4 to share with stakeholders' },
  { icon: Sparkles, title: 'Live AI Insights', desc: 'Experience real-time AI alerts and productivity recommendations' },
];

const DemoShowcase = () => (
  <section id="features" className="relative py-28" data-testid="mktg-demo-showcase">
    <div className="absolute inset-0 bg-gradient-to-b from-[#0D1728] to-[#0B1120]" />
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-green-400 mb-3">Self-Serve Demo</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Experience the product before you commit</h2>
        <p className="text-gray-400 max-w-xl mx-auto">Try our fully interactive demo with real AI features — tailored for both small teams and large organizations.</p>
      </motion.div>

      <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {demoFeatures.map((f) => (
          <motion.div key={f.title} variants={cardVariant}
            className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-green-500/20 transition-colors text-center group">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500/15 transition-colors">
              <f.icon className="w-5 h-5 text-green-400" />
            </div>
            <h3 className="text-white font-semibold text-sm mb-2">{f.title}</h3>
            <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </motion.div>

      <motion.div {...fadeUp} className="flex flex-col items-center">
        <a href="https://app.d3command.com/demo"
          className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold text-base shadow-[0_0_24px_rgba(34,197,94,0.25)] hover:shadow-[0_0_32px_rgba(34,197,94,0.4)] transition-all">
          <Play className="w-5 h-5" /> Launch Interactive Demo
        </a>
        <div className="flex items-center gap-6 mt-6 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><CheckSquare className="w-3.5 h-3.5 text-green-400" /> No signup required</span>
          <span className="flex items-center gap-1.5"><CheckSquare className="w-3.5 h-3.5 text-green-400" /> Sample data auto-resets</span>
          <span className="flex items-center gap-1.5"><CheckSquare className="w-3.5 h-3.5 text-green-400" /> Full product access</span>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Services
   ═══════════════════════════════════════════════ */
const services = [
  { id: 'ai-workflow', icon: Brain, title: 'AI Workflow Automation', desc: 'Stop wasting time on repetitive tasks. We build custom AI agents that handle your heavy lifting and speed up your daily operations.' },
  { id: 'web-mobile', icon: Code, title: 'Custom Web & Mobile Apps', desc: 'Build something your users love. We engineer high-performance apps for every device, focusing on speed, design, and usability.' },
  { id: 'digitization', icon: Settings, title: 'Business Process Digitization', desc: 'Move your workflows into the modern age. We transform manual processes into streamlined digital systems that grow with you.' },
  { id: 'operations', icon: Layers, title: 'Operations & Governance', desc: 'Stay in control of your growth. Our platforms provide the tracking and visibility you need to keep your team aligned and compliant.' },
  { id: 'dashboards', icon: BarChart3, title: 'Dashboards & Analytics', desc: 'See your business clearly. We build real-time reporting tools that turn raw data into actionable insights for better decision-making.' },
  { id: 'integration', icon: Server, title: 'API & Systems Integration', desc: 'Connect your entire tech stack. We bridge the gap between your CRM, internal tools, and data sources for a unified workflow.' },
  { id: 'deployment', icon: Globe, title: 'App Store & Cloud Deployment', desc: 'Launch with total confidence. We manage the full release process for iOS, Android, and web, handling every technical and compliance detail.' },
  { id: 'public-sector', icon: Landmark, title: 'Public Sector & Government Solutions', desc: 'Secure, process-driven software for regulated environments. We build for accountability, audit readiness, and cross-team visibility.' },
  { id: 'support', icon: Shield, title: 'Managed Support & Maintenance', desc: 'Long-term stability for your assets, including specialized technical support tailored for public sector and enterprise security standards.' },
];

const Services = () => (
  <section id="services" className="relative py-28" data-testid="mktg-services">
    <div className="absolute inset-0 bg-[#0D1728]" />
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-3">Services</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Custom software and AI solutions for growth-oriented businesses</h2>
        <p className="text-gray-400 max-w-xl mx-auto">Enterprise-grade solutions from the team behind the D3 Command platform.</p>
      </motion.div>

      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {services.map((s) => (
          <motion.div key={s.id} variants={cardVariant}
            className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/20 transition-all group flex flex-col">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/15 transition-colors">
              <s.icon className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="text-white font-semibold text-base mb-2">{s.title}</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-grow">{s.desc}</p>
            <a 
              href={`#demo?service=${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                const demoSection = document.getElementById('demo');
                const serviceSelect = document.getElementById('service-select');
                if (serviceSelect) serviceSelect.value = s.id;
                if (demoSection) demoSection.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium hover:bg-cyan-500/20 transition-colors"
            >
              Request Custom Quote <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Industries
   ═══════════════════════════════════════════════ */
const industries = [
  { icon: Building2, title: 'Enterprise Operations', desc: 'Structured execution for cross-functional teams at scale.' },
  { icon: Shield, title: 'Compliance & Risk', desc: 'Audit-ready workflows with built-in traceability and controls.' },
  { icon: Briefcase, title: 'Financial Services', desc: 'Governance, reporting, and operational visibility for regulated finance.' },
  { icon: Heart, title: 'Healthcare', desc: 'Secure, compliant platforms for care coordination and administration.' },
  { icon: Landmark, title: 'Public Sector & Government', desc: 'Process-driven solutions for contractors and government agencies.' },
  { icon: Code, title: 'Product & Engineering', desc: 'AI-powered sprint management, risk tracking, and delivery accountability.' },
];

const Industries = () => (
  <section id="industries" className="relative py-28" data-testid="mktg-industries">
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Industries</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Built for teams across enterprise and regulated environments</h2>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {industries.map((ind) => (
          <motion.div key={ind.title} variants={cardVariant}
            className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/20 transition-colors">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
              <ind.icon className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm mb-1">{ind.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{ind.desc}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Why D3 Command
   ═══════════════════════════════════════════════ */
const benefits = [
  { icon: Zap, title: 'Automate Execution', desc: 'Convert conversations into structured tasks and tracked outcomes.' },
  { icon: Eye, title: 'Gain Visibility', desc: 'Real-time dashboards show exactly where work stands across teams.' },
  { icon: Settings, title: 'Reduce Manual Work', desc: 'Eliminate repetitive documentation, reporting, and status updates.' },
  { icon: Users, title: 'Improve Accountability', desc: 'Every task, decision, and risk is assigned, tracked, and auditable.' },
  { icon: Shield, title: 'Strengthen Governance', desc: 'Role-based access, audit logs, and compliance-ready architecture.' },
  { icon: Layers, title: 'Modernize Operations', desc: 'Replace spreadsheets and manual processes with intelligent workflows.' },
];

const WhyD3 = () => (
  <section className="relative py-28" data-testid="mktg-why">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/[0.015] to-transparent" />
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-3">Why D3 Command</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Why leaders choose D3 Command</h2>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {benefits.map((b) => (
          <motion.div key={b.title} variants={cardVariant} className="text-center p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/15 transition-colors">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 flex items-center justify-center mx-auto mb-4">
              <b.icon className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="text-white font-semibold text-sm mb-1.5">{b.title}</h3>
            <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: How We Work
   ═══════════════════════════════════════════════ */
const engagements = [
  { num: '01', title: 'Rapid Solution Deployment', desc: 'Onboard your team and configure your custom solution with guided enablement for immediate impact.' },
  { num: '02', title: 'Custom Solution Development', desc: 'Build bespoke applications, dashboards, or automation systems tailored to your workflows and data.' },
  { num: '03', title: 'Workflow Integration & Modernization', desc: 'Connect and modernize existing tools, systems, and processes into a unified operational layer.' },
  { num: '04', title: 'Enterprise Enablement & Advisory', desc: 'Strategic guidance on AI adoption, workflow optimization, and operational transformation.' },
];

const HowWeWork = () => (
  <section className="relative py-28" data-testid="mktg-how">
    <div className="absolute inset-0 bg-[#0D1728]" />
    <div className="relative max-w-5xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Engagement Models</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Flexible engagement models designed for businesses of all scales</h2>
        <p className="text-gray-400 max-w-lg mx-auto">Customized delivery models to fit your specific budget, timeline, and technical requirements.</p>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {engagements.map((e) => (
          <motion.div key={e.num} variants={cardVariant}
            className="flex gap-5 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/20 transition-colors">
            <span className="text-3xl font-bold bg-gradient-to-b from-indigo-400/30 to-transparent bg-clip-text text-transparent flex-shrink-0">{e.num}</span>
            <div>
              <h3 className="text-white font-semibold text-sm mb-1.5">{e.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{e.desc}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Trust & Security
   ═══════════════════════════════════════════════ */
const trustItems = [
  { icon: Brain, title: 'AI Meeting Intelligence', desc: 'Transcribe, analyze, and extract decisions, tasks, and risks from every conversation automatically.' },
  { icon: BarChart3, title: 'Real-Time Execution Tracking', desc: 'Live dashboards show task progress, risk status, and operational metrics across all teams.' },
  { icon: Building2, title: 'Multi-Tenant Architecture', desc: 'Complete data isolation between organizations with separate environments and access controls.' },
  { icon: Palette, title: 'White-Label Email Branding', desc: 'Customize email display name, reply-to address, logo, accent colors, and footer text per organization.' },
  { icon: Lock, title: 'Role-Based Access Control', desc: 'Granular permissions ensure every user sees only what they should — Admin, Manager, and Member tiers.' },
  { icon: FileText, title: 'Audit Logging & Compliance', desc: 'Every action is recorded with timestamps, user attribution, and full traceability for GDPR compliance.' },
];

const Trust = () => (
  <section className="relative py-28" data-testid="mktg-trust">
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">Enterprise Readiness</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Built for secure execution and professional governance</h2>
        <p className="text-gray-400 max-w-lg mx-auto">Access high-level security, compliance, and operational control, regardless of your company size.</p>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {trustItems.map((t) => (
          <motion.div key={t.title} variants={cardVariant}
            className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-indigo-500/15 transition-colors">
            <t.icon className="w-5 h-5 text-indigo-400 mb-3" />
            <h3 className="text-white font-semibold text-sm mb-1.5">{t.title}</h3>
            <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
          </motion.div>
        ))}
      </motion.div>
      <motion.div {...fadeUp} className="text-center mt-10">
        <a href="https://app.d3command.com/demo" className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors">
          <Play className="w-4 h-4" /> Experience it in our interactive demo
        </a>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Pricing CTA
   ═══════════════════════════════════════════════ */
const plans = [
  { name: 'Starter', price: '$29', period: '/mo', desc: 'For small teams getting started', features: ['Up to 10 users', 'AI meeting processing', 'Task & risk extraction', 'Real-time dashboards', 'Activity digest emails', 'Email support'], highlight: false },
  { name: 'Growth', price: '$79', period: '/mo', desc: 'For scaling operations teams', features: ['Up to 50 users', 'Everything in Starter', 'Threaded comments & @mentions', 'Advanced analytics', 'Custom integrations', 'Priority support'], highlight: true },
  { name: 'Enterprise', price: 'Custom', period: '', desc: 'For organizations with advanced needs', features: ['Unlimited users', 'Everything in Growth', 'White-label email branding', 'Dedicated environment', 'Custom workflows & SLA', 'Onboarding & training'], highlight: false },
];

const Pricing = () => (
  <section id="pricing" className="relative py-28" data-testid="mktg-pricing">
    <div className="absolute inset-0 bg-[#0D1728]" />
    <div className="relative max-w-5xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-3">Pricing</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">D3 Command Platform Pricing</h2>
      </motion.div>
      <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => (
          <motion.div key={p.name} variants={cardVariant}
            className={`p-6 rounded-2xl border transition-colors ${p.highlight ? 'bg-indigo-500/[0.06] border-indigo-500/25' : 'bg-white/[0.02] border-white/[0.06]'}`}>
            <h3 className="text-white font-semibold mb-1">{p.name}</h3>
            <p className="text-gray-500 text-xs mb-4">{p.desc}</p>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="text-3xl font-bold text-white">{p.price}</span>
              {p.period && <span className="text-sm text-gray-500">{p.period}</span>}
            </div>
            <ul className="space-y-2 mb-6">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-gray-400">
                  <CheckSquare className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <a href={p.name === 'Enterprise' ? '#demo' : 'https://app.d3command.com/signup'}
              className={`block text-center py-2.5 rounded-xl text-sm font-medium transition-colors ${p.highlight ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-white/[0.06] hover:bg-white/[0.1] text-gray-300'}`}>
              {p.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'}
            </a>
          </motion.div>
        ))}
      </motion.div>
      
      {/* Custom Solution Callout */}
      <motion.div {...fadeUp} className="mt-12 p-8 rounded-2xl bg-[#0f1629] border border-white/[0.06] text-center">
        <h3 className="text-xl font-bold text-white mb-3">Need a custom solution?</h3>
        <p className="text-gray-400 text-sm max-w-xl mx-auto mb-6">
          Looking for a bespoke web app, mobile build, or enterprise-wide automation? We offer project-based pricing tailored to your requirements.
        </p>
        <a href="#demo" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition-colors">
          Get a Custom Quote <ArrowRight className="w-4 h-4" />
        </a>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Trusted Technologies
   ═══════════════════════════════════════════════ */
const TrustedTechnologies = () => (
  <section className="relative py-16 border-t border-white/5" data-testid="mktg-trusted-tech">
    <div className="relative max-w-4xl mx-auto px-6">
      <motion.div {...fadeUp} className="text-center mb-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Trusted Technologies</p>
        <p className="text-sm text-gray-400">Built on enterprise-grade infrastructure and integrations</p>
      </motion.div>
      <motion.div {...fadeUp} className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
        {/* Stripe */}
        <div className="flex items-center gap-2 opacity-50 hover:opacity-80 transition-opacity grayscale hover:grayscale-0">
          <svg viewBox="0 0 24 24" className="h-6 fill-gray-400">
            <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
          </svg>
          <span className="text-sm font-medium text-gray-400">Stripe</span>
        </div>
        {/* Google Cloud */}
        <div className="flex items-center gap-2 opacity-50 hover:opacity-80 transition-opacity grayscale hover:grayscale-0">
          <svg viewBox="0 0 24 24" className="h-6 fill-gray-400">
            <path d="M12.19 2.38a9.344 9.344 0 0 0-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.11-.247 10.941l.006-.007-.007.03a6.717 6.717 0 0 0 4.077 1.356h5.173l.03.03h5.192c6.687.053 9.376-8.605 3.835-12.35a9.365 9.365 0 0 0-8.825-6.893zM8.073 19.817a4.883 4.883 0 0 1-2.411-.61l.004-.001c-2.946-1.622-3.404-5.715-.877-7.878a4.964 4.964 0 0 1 .877-.62c.11-.06.224-.115.34-.167l.06-.028a9.31 9.31 0 0 0 6.04 8.893l-.014.014a4.907 4.907 0 0 1-4.019.397zm9.138-.001a7.48 7.48 0 0 1-7.023-5.015 7.504 7.504 0 0 1 5.186-9.378 7.478 7.478 0 0 1 8.564 4.345c.247.586.413 1.2.494 1.829.084.628.08 1.267-.003 1.896a7.476 7.476 0 0 1-7.218 6.323z"/>
          </svg>
          <span className="text-sm font-medium text-gray-400">Google Cloud</span>
        </div>
        {/* AWS */}
        <div className="flex items-center gap-2 opacity-50 hover:opacity-80 transition-opacity grayscale hover:grayscale-0">
          <svg viewBox="0 0 24 24" className="h-6 fill-gray-400">
            <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 0 1-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 0 1-.287-.375 6.18 6.18 0 0 1-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.031-.863.103-.296.072-.583.16-.863.279a2.07 2.07 0 0 1-.255.112.39.39 0 0 1-.104.016c-.096 0-.144-.072-.144-.216v-.391c0-.112.016-.2.056-.256a.62.62 0 0 1 .208-.16c.28-.143.616-.263 1.006-.359.391-.096.807-.144 1.246-.144.95 0 1.644.216 2.091.647.439.43.662 1.085.662 1.963v2.586h.016l-.016.016zm-3.24 1.214c.263 0 .535-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 0 0-.735-.136 6.02 6.02 0 0 0-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.12 0-.2-.016-.255-.056-.056-.032-.104-.112-.144-.24l-1.597-5.252a1.33 1.33 0 0 1-.064-.344c0-.136.072-.208.216-.208h.783c.128 0 .216.016.264.056.055.032.096.112.136.24l1.142 4.503 1.061-4.503c.032-.136.072-.208.128-.24a.506.506 0 0 1 .272-.056h.638c.128 0 .216.016.272.056.055.032.096.112.128.24l1.077 4.567 1.173-4.567c.04-.136.088-.208.144-.24a.52.52 0 0 1 .263-.056h.743c.144 0 .216.064.216.208a.61.61 0 0 1-.016.135 1.28 1.28 0 0 1-.048.208l-1.637 5.252c-.04.128-.088.208-.144.24-.055.032-.143.056-.263.056h-.686c-.128 0-.216-.016-.272-.056-.055-.04-.096-.112-.128-.248l-1.053-4.39-1.045 4.382c-.032.136-.072.208-.128.248-.056.04-.152.056-.272.056h-.686zm10.256.215c-.415 0-.83-.048-1.229-.144-.399-.096-.71-.2-.918-.319-.128-.072-.216-.152-.248-.232a.574.574 0 0 1-.048-.224v-.407c0-.144.056-.216.16-.216a.42.42 0 0 1 .128.016c.04.008.1.032.168.072.255.128.543.232.863.311.327.08.646.12.973.12.511 0 .91-.088 1.19-.271.279-.184.423-.455.423-.822 0-.24-.071-.439-.216-.591-.144-.152-.415-.288-.807-.415l-1.157-.36c-.583-.184-1.014-.456-1.277-.816-.263-.36-.399-.767-.399-1.214 0-.351.08-.663.232-.942.152-.279.359-.519.622-.71.263-.2.567-.35.918-.455a4.1 4.1 0 0 1 1.133-.152c.199 0 .407.008.606.032.2.016.39.048.574.08.176.04.343.08.495.128a1.5 1.5 0 0 1 .359.152c.104.056.176.112.224.176.048.056.072.128.072.232v.375c0 .144-.056.216-.16.216a.82.82 0 0 1-.288-.088 3.47 3.47 0 0 0-1.469-.295c-.463 0-.83.072-1.093.224-.263.152-.399.392-.399.727 0 .24.08.447.24.607.16.16.447.32.862.455l1.133.36c.574.184.99.44 1.237.767.247.327.375.702.375 1.117 0 .359-.08.686-.231.974a2.24 2.24 0 0 1-.631.71 2.79 2.79 0 0 1-.942.448 4.38 4.38 0 0 1-1.165.16h-.003zm1.382 3.75c-4.013 1.53-9.836 2.343-14.849.678-.247-.088-.503.112-.27.335 1.31 1.108 5.586 3.544 11.109 3.544 5.524 0 7.525-2.846 7.814-3.287.288-.446-.016-.711-.304-.543-.998.583-2.992 1.485-6.5 1.785z"/>
          </svg>
          <span className="text-sm font-medium text-gray-400">AWS</span>
        </div>
        {/* MongoDB */}
        <div className="flex items-center gap-2 opacity-50 hover:opacity-80 transition-opacity grayscale hover:grayscale-0">
          <svg viewBox="0 0 24 24" className="h-6 fill-gray-400">
            <path d="M17.193 9.555c-1.264-5.58-4.252-7.414-4.573-8.115-.28-.394-.53-.954-.735-1.44-.036.495-.055.685-.523 1.184-.723.566-4.438 3.682-4.74 10.02-.282 5.912 4.27 9.435 4.888 9.884l.07.05A73.49 73.49 0 0111.91 24h.481c.114-1.032.284-2.056.51-3.07.417-.296.604-.463.85-.693a11.342 11.342 0 003.639-8.464c.01-.814-.103-1.662-.197-2.218zm-5.336 8.195s0-8.291.275-8.29c.213 0 .49 10.695.49 10.695-.381-.045-.765-1.76-.765-2.405z"/>
          </svg>
          <span className="text-sm font-medium text-gray-400">MongoDB</span>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Demo Form
   ═══════════════════════════════════════════════ */
const serviceOptions = [
  { id: '', label: 'Select a service (optional)' },
  { id: 'ai-workflow', label: 'AI Workflow Automation' },
  { id: 'web-mobile', label: 'Custom Web & Mobile Apps' },
  { id: 'digitization', label: 'Business Process Digitization' },
  { id: 'operations', label: 'Operations & Governance' },
  { id: 'dashboards', label: 'Dashboards & Analytics' },
  { id: 'integration', label: 'API & Systems Integration' },
  { id: 'deployment', label: 'App Store & Cloud Deployment' },
  { id: 'public-sector', label: 'Public Sector & Government Solutions' },
  { id: 'support', label: 'Managed Support & Maintenance' },
];

const DemoSection = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || APP_URL;
      const res = await fetch(`${API}/api/leads/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Submission failed (${res.status})`);
      }
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" className="relative py-28" data-testid="mktg-demo">
      <div className="relative max-w-2xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to build your next big idea?</h2>
          <p className="text-gray-400">Tell us about your project or request a platform demo. We&apos;ll show you how D3 Command can help.</p>
        </motion.div>
        {sent ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="text-center p-10 rounded-2xl bg-white/[0.03] border border-green-500/20">
            <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckSquare className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">Inquiry Received</h3>
            <p className="text-gray-400 text-sm">Our team will be in touch shortly.</p>
          </motion.div>
        ) : (
          <motion.form {...fadeUp} onSubmit={handleSubmit} className="space-y-4 p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="mktg-demo-error">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input type="text" required placeholder="Full Name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40" data-testid="mktg-demo-name" />
              <input type="email" required placeholder="Work Email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40" data-testid="mktg-demo-email" />
            </div>
            <input type="text" required placeholder="Company" value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40" data-testid="mktg-demo-company" />
            <select 
              id="service-select"
              value={form.service} 
              onChange={(e) => setForm(p => ({ ...p, service: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-indigo-500/40 appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
              data-testid="mktg-demo-service"
            >
              {serviceOptions.map(opt => (
                <option key={opt.id} value={opt.id} className="bg-[#0A0A0B] text-white">{opt.label}</option>
              ))}
            </select>
            <textarea placeholder="Tell us about your project or goals..." value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40 resize-none" data-testid="mktg-demo-message" />
            <button type="submit" disabled={submitting} className="w-full py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors" data-testid="mktg-demo-submit">
              {submitting ? 'Sending...' : 'Send Inquiry'}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   SECTION: Final CTA
   ═══════════════════════════════════════════════ */
const FinalCTA = () => (
  <section className="relative py-24" data-testid="mktg-final-cta">
    <div className="absolute inset-0 bg-gradient-to-t from-[#0D1728] to-transparent" />
    <div className="relative max-w-3xl mx-auto px-6 text-center">
      <motion.div {...fadeUp}>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to build something great?</h2>
        <p className="text-gray-400 mb-8 max-w-lg mx-auto">From AI-powered platforms to custom web and mobile apps — D3 Command is your execution partner.</p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a href="#demo" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all shadow-[0_0_24px_rgba(79,70,229,0.2)]">
            Get a Quote <ArrowRight className="w-4 h-4" />
          </a>
          <a href="mailto:support@d3command.com" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white/[0.06] border border-white/10 hover:border-white/20 text-white font-medium text-sm transition-all">
            <Mail className="w-4 h-4" /> Talk to Us
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SECTION: Footer
   ═══════════════════════════════════════════════ */
const footerLinks = {
  Product: [
    { label: 'Platform', href: '#platform' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'View Demo', href: 'https://app.d3command.com/demo' },
    { label: 'Login', href: 'https://app.d3command.com/login' },
  ],
  Services: [
    { label: 'AI Automation', href: '#services' },
    { label: 'Custom Development', href: '#services' },
    { label: 'Modernization', href: '#services' },
    { label: 'Integration', href: '#services' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '#demo' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms-of-service' },
  ],
};

const MktgFooter = () => (
  <footer className="relative border-t border-white/[0.05] bg-[#0B1120] py-16" data-testid="mktg-footer">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
        {/* Brand col */}
        <div className="col-span-2 md:col-span-1">
          <div className="mb-4" data-testid="mktg-footer-logo">
            <D3CommandLogo size="sm" />
          </div>
          <p className="text-gray-600 text-xs leading-relaxed mb-4">AI-powered execution, automation, and enterprise software solutions.</p>
          <div className="space-y-1.5 text-xs text-gray-500">
            <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> support@d3command.com</div>
            <div className="flex items-center gap-2"><Globe className="w-3 h-3" /> d3command.com</div>
          </div>
        </div>
        {/* Link cols */}
        {Object.entries(footerLinks).map(([title, links]) => (
          <div key={title}>
            <h4 className="text-white text-xs font-semibold uppercase tracking-wider mb-4">{title}</h4>
            <ul className="space-y-2.5">
              {links.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="text-gray-500 hover:text-gray-300 text-xs transition-colors">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/[0.05] pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-center sm:text-left" data-testid="mktg-footer-legal">
          <p className="text-xs text-gray-500">&copy; 2026 D3 Command. All rights reserved.</p>
          <p className="text-[11px] text-gray-600 mt-0.5">D3 Command is a product of D3 Global Holdings LLC.</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <a href="/privacy-policy" className="hover:text-gray-400 transition-colors">Privacy</a>
          <a href="/terms-of-service" className="hover:text-gray-400 transition-colors">Terms</a>
          <a href="/cookie-policy" className="hover:text-gray-400 transition-colors">Cookies</a>
        </div>
      </div>
    </div>
  </footer>
);

/* ═══════════════════════════════════════════════
   NAVBAR (Marketing)
   ═══════════════════════════════════════════════ */
const MktgNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const handleScroll = () => setScrolled(window.scrollY > 40);
  if (typeof window !== 'undefined') window.addEventListener('scroll', handleScroll, { passive: true });

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0B1120]/90 backdrop-blur-xl border-b border-white/[0.05]' : ''}`} data-testid="mktg-nav">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center" data-testid="mktg-nav-logo">
          <D3CommandLogo size="default" />
        </a>
        <div className="hidden md:flex items-center gap-8">
          <a href="#platform" className="text-xs text-gray-400 hover:text-white transition-colors">Platform</a>
          <a href="#services" className="text-xs text-gray-400 hover:text-white transition-colors">Services</a>
          <a href="#industries" className="text-xs text-gray-400 hover:text-white transition-colors">Industries</a>
          <a href="#pricing" className="text-xs text-gray-400 hover:text-white transition-colors">Pricing</a>
        </div>
        <div className="flex items-center gap-3">
          <a href="https://app.d3command.com/login" className="text-xs text-gray-400 hover:text-white transition-colors hidden sm:block">Sign In</a>
          <a href="#demo" className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold transition-colors" data-testid="mktg-nav-demo">
            Get a Quote
          </a>
        </div>
      </div>
    </nav>
  );
};

/* ═══════════════════════════════════════════════
   MAIN PAGE EXPORT
   ═══════════════════════════════════════════════ */
export default function MarketingPage() {
  return (
    <div className="min-h-screen bg-[#0B1120] text-white" data-testid="marketing-page">
      <MktgNav />
      <Hero />
      <FlagshipPlatform />
      <DemoShowcase />
      <Services />
      <Industries />
      <WhyD3 />
      <HowWeWork />
      <Trust />
      <Pricing />
      <TrustedTechnologies />
      <DemoSection />
      <FinalCTA />
      <MktgFooter />
      <ChatWidget />
    </div>
  );
}
